/*
  OK FBI — Stream Platforms Manager (Twitch, Kick, TikTok)
  لوحة ربط وإدارة منصات البث المباشر الرسمية:
    • Kick: OAuth2.1 PKCE + Webhook (RSA signature)
    • Twitch: OAuth2 + Helix API + EventSub Webhooks
    • TikTok: Webhook Bridge + Live Comment Feed
*/
import React, { useState, useEffect, useCallback } from "react";
import { api, BASE_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  Radio, Wifi, WifiOff, RefreshCw, Copy, Check, ExternalLink,
  AlertCircle, CheckCircle2, ShieldCheck, Zap, Activity
} from "lucide-react";

export function StreamPlatformsManager() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [config, setConfig] = useState({ twitch: { configured: false }, kick: { configured: false } });
  
  // Platform states
  const [twitchConn, setTwitchConn] = useState(null);
  const [kickConn, setKickConn] = useState(null);
  const [tiktokConn, setTiktokConn] = useState(null);
  
  const [copiedKey, setCopiedKey] = useState("");

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success("تم النسخ إلى الحافظة");
    setTimeout(() => setCopiedKey(""), 2500);
  };

  const loadAll = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      // 1. Config status
      const { data: cfg } = await api.get("/stream/platforms").catch(() => ({ data: {} }));
      if (cfg) setConfig(cfg);

      // 2. Twitch status
      const { data: tw } = await api.get("/twitch/status").catch(() => ({ data: {} }));
      setTwitchConn(tw?.connection || null);

      // 3. Kick status
      const { data: kc } = await api.get("/kick/status").catch(() => ({ data: {} }));
      setKickConn(kc?.connection || null);

      // 4. TikTok status
      const { data: tt } = await api.get("/tiktok/status").catch(() => ({ data: {} }));
      setTiktokConn(tt || null);
    } catch (e) {
      console.error("Error loading stream platforms:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Check URL query parameters for OAuth returns (e.g. ?stream=kick_connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stream = params.get("stream");
    const streamError = params.get("stream_error");

    if (stream === "kick_connected") {
      toast.success("🎉 تم ربط حساب Kick بنجاح!");
      loadAll(true);
    } else if (stream === "twitch_connected") {
      toast.success("🎉 تم ربط حساب Twitch بنجاح!");
      loadAll(true);
    } else if (stream === "tiktok_connected") {
      toast.success("🎉 تم ربط حساب TikTok بنجاح!");
      loadAll(true);
    } else if (streamError) {
      toast.error(`فشل ربط المنصة: ${streamError}`);
    }
  }, [loadAll]);

  // Actions
  const handleKickDisconnect = async () => {
    try {
      await api.post("/kick/disconnect");
      toast.success("تم قطع الاتصال بـ Kick");
      loadAll(true);
    } catch {
      toast.error("فشل قطع الاتصال بـ Kick");
    }
  };

  const handleTwitchDisconnect = async () => {
    try {
      await api.post("/twitch/disconnect");
      toast.success("تم قطع الاتصال بـ Twitch");
      loadAll(true);
    } catch {
      toast.error("فشل قطع الاتصال بـ Twitch");
    }
  };

  const handleTwitchSync = async () => {
    try {
      const { data } = await api.post("/twitch/subscriptions/sync");
      toast.success("تمت مزامنة تنبيهات EventSub بنجاح");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت مزامنة تنبيهات Twitch");
    }
  };

  const handleTiktokDisconnect = async () => {
    try {
      await api.post("/tiktok/disconnect");
      toast.success("تم قطع اتصال TikTok");
      loadAll(true);
    } catch {
      toast.error("فشل قطع الاتصال");
    }
  };

  const handleTiktokReconnect = async () => {
    try {
      await api.post("/tiktok/reconnect");
      toast.success("تمت إعادة تشغيل استقبال التفاعل");
      loadAll(true);
    } catch {
      toast.error("فشلت إعادة الاتصال");
    }
  };

  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://okfbi.com";
  const apiBase = (BASE_URL || "").replace(/\/+$/, "");
  const token = (typeof window !== "undefined" ? localStorage.getItem("okfbi_token") : "") || "";
  const authQuery = token ? `?token=${encodeURIComponent(token)}` : "";

  const kickOAuthUrl = `${apiBase}/api/kick/start${authQuery}`;
  const twitchOAuthUrl = `${apiBase}/api/twitch/start${authQuery}`;
  const tiktokOAuthUrl = `${apiBase}/api/tiktok/start${authQuery}`;

  return (
    <div dir="rtl" className="space-y-6" data-testid="stream-platforms-manager">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border border-neutral-800 rounded-xl bg-neutral-950/80 backdrop-blur-sm">
        <div>
          <div className="flex items-center gap-2">
            <Radio className="text-yellow-400 animate-pulse" size={20} />
            <h2 className="text-lg lg:text-xl font-black font-cairo text-white">
              ربط وتفعيل منصات البث المباشر
            </h2>
          </div>
          <p className="text-xs text-neutral-400 font-cairo mt-1">
            اربط حساباتك الرسمية على Kick و Twitch و TikTok لاستقبال المتابعات والأحداث وتعليقات المشاهدين أثناء التحقيق.
          </p>
        </div>
        <button
          onClick={() => loadAll()}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-900 hover:bg-neutral-800 border border-neutral-700 text-neutral-300 rounded-lg text-xs font-cairo transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin text-yellow-400" : ""} />
          تحديث الحالة
        </button>
      </div>

      {/* Grid of Platforms */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        
        {/* ─── 1. KICK PLATFORM CARD ─── */}
        <div className="border border-emerald-900/60 rounded-xl bg-neutral-950/90 overflow-hidden flex flex-col justify-between shadow-[0_0_25px_rgba(16,185,129,0.06)]">
          <div>
            {/* Platform Banner */}
            <div className="p-4 border-b border-neutral-800/80 bg-gradient-to-l from-emerald-950/40 to-transparent flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#53FC18]/10 border border-[#53FC18]/30 flex items-center justify-center font-black text-[#53FC18] text-sm">
                  K
                </div>
                <div>
                  <div className="font-bold text-sm text-white font-cairo flex items-center gap-1.5">
                    منصة Kick
                    <span className="text-[10px] text-emerald-400 font-mono bg-emerald-950/60 border border-emerald-800 px-1.5 py-0.2 rounded">
                      OAuth 2.1
                    </span>
                  </div>
                  <div className="text-[10px] text-neutral-400 font-cairo">developers.kick.com</div>
                </div>
              </div>

              {/* Status Badge */}
              {kickConn?.connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-emerald-700 text-emerald-300 bg-emerald-950/50">
                  <CheckCircle2 size={11} /> متصل
                </span>
              ) : config.kick?.configured ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                  غير متصل
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-amber-800 text-amber-400 bg-amber-950/40">
                  <AlertCircle size={10} /> بانتظار المفاتيح
                </span>
              )}
            </div>

            {/* Platform Body */}
            <div className="p-4 space-y-3">
              {kickConn?.connected ? (
                <div className="p-3 border border-emerald-900/50 rounded-lg bg-emerald-950/20 space-y-1.5 text-xs font-cairo">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">اسم الحساب:</span>
                    <span className="text-emerald-300 font-bold">{kickConn.display_name || kickConn.login}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">معرّف البث (ID):</span>
                    <span className="text-neutral-300 font-mono">{kickConn.broadcaster_user_id || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">حالة البث:</span>
                    <span className={`font-bold ${kickConn.live_status === "online" ? "text-emerald-400" : "text-neutral-400"}`}>
                      {kickConn.live_status === "online" ? "🟢 مباشر" : "⚪ غير مباشر"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-neutral-400 font-cairo leading-relaxed">
                  اربط حسابك لتفعيل استقبال أحداث البث المباشر وتفاعل القناة عبر التحقق الأمني الرسمي بـ RSA و PKCE.
                </p>
              )}

              {/* Webhook Endpoint Info */}
              <div className="pt-2 border-t border-neutral-900 space-y-1">
                <div className="text-[10px] text-neutral-500 font-cairo flex items-center justify-between">
                  <span>رابط استلام الويب هوك (Kick Webhook):</span>
                </div>
                <div className="flex items-center gap-1 bg-black/50 border border-neutral-800 rounded px-2 py-1 text-[11px] font-mono text-neutral-300">
                  <span className="truncate flex-1" dir="ltr">{currentOrigin}/api/kick/webhook</span>
                  <button
                    onClick={() => copyToClipboard(`${currentOrigin}/api/kick/webhook`, "kick_wh")}
                    className="text-neutral-400 hover:text-white shrink-0 p-1"
                    title="نسخ الرابط"
                  >
                    {copiedKey === "kick_wh" ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Actions */}
          <div className="p-4 border-t border-neutral-900 bg-neutral-950/40">
            {kickConn?.connected ? (
              <button
                onClick={handleKickDisconnect}
                className="w-full py-2 px-3 border border-red-900/60 hover:bg-red-950/30 text-red-400 rounded-lg text-xs font-cairo transition-colors flex items-center justify-center gap-1.5"
              >
                <WifiOff size={13} /> قطع الاتصال بـ Kick
              </button>
            ) : (
              <a
                href={kickOAuthUrl}
                className="w-full py-2 px-3 bg-[#53FC18]/15 hover:bg-[#53FC18]/25 border border-[#53FC18]/50 text-[#53FC18] rounded-lg text-xs font-black font-cairo transition-colors flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(83,252,24,0.15)]"
              >
                <ExternalLink size={13} /> تفعيل وربط حساب Kick الآن
              </a>
            )}
          </div>
        </div>

        {/* ─── 2. TWITCH PLATFORM CARD ─── */}
        <div className="border border-purple-900/60 rounded-xl bg-neutral-950/90 overflow-hidden flex flex-col justify-between shadow-[0_0_25px_rgba(168,85,247,0.06)]">
          <div>
            {/* Platform Banner */}
            <div className="p-4 border-b border-neutral-800/80 bg-gradient-to-l from-purple-950/40 to-transparent flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#9146FF]/15 border border-[#9146FF]/30 flex items-center justify-center font-black text-[#A970FF] text-sm">
                  T
                </div>
                <div>
                  <div className="font-bold text-sm text-white font-cairo flex items-center gap-1.5">
                    منصة Twitch
                    <span className="text-[10px] text-purple-400 font-mono bg-purple-950/60 border border-purple-800 px-1.5 py-0.2 rounded">
                      Helix API
                    </span>
                  </div>
                  <div className="text-[10px] text-neutral-400 font-cairo">id.twitch.tv</div>
                </div>
              </div>

              {/* Status Badge */}
              {twitchConn?.connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-purple-700 text-purple-300 bg-purple-950/50">
                  <CheckCircle2 size={11} /> متصل
                </span>
              ) : config.twitch?.configured ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                  غير متصل
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-amber-800 text-amber-400 bg-amber-950/40">
                  <AlertCircle size={10} /> بانتظار المفاتيح
                </span>
              )}
            </div>

            {/* Platform Body */}
            <div className="p-4 space-y-3">
              {twitchConn?.connected ? (
                <div className="p-3 border border-purple-900/50 rounded-lg bg-purple-950/20 space-y-1.5 text-xs font-cairo">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">اسم القناة:</span>
                    <span className="text-purple-300 font-bold">{twitchConn.display_name || twitchConn.login}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">معرّف المذيع:</span>
                    <span className="text-neutral-300 font-mono">{twitchConn.broadcaster_user_id || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">حالة البث:</span>
                    <span className={`font-bold ${twitchConn.live_status === "online" ? "text-emerald-400" : "text-neutral-400"}`}>
                      {twitchConn.live_status === "online" ? "🟢 مباشر" : "⚪ غير مباشر"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-neutral-400 font-cairo leading-relaxed">
                  ربط القناة الرسمية عبر OAuth واستقبال تنبيهات المتابعين وبدء البث عبر EventSub المشفر.
                </p>
              )}

              {/* EventSub Info */}
              <div className="pt-2 border-t border-neutral-900 space-y-1">
                <div className="text-[10px] text-neutral-500 font-cairo flex items-center justify-between">
                  <span>رابط الـ EventSub Callback:</span>
                </div>
                <div className="flex items-center gap-1 bg-black/50 border border-neutral-800 rounded px-2 py-1 text-[11px] font-mono text-neutral-300">
                  <span className="truncate flex-1" dir="ltr">{currentOrigin}/api/twitch/eventsub</span>
                  <button
                    onClick={() => copyToClipboard(`${currentOrigin}/api/twitch/eventsub`, "tw_wh")}
                    className="text-neutral-400 hover:text-white shrink-0 p-1"
                    title="نسخ الرابط"
                  >
                    {copiedKey === "tw_wh" ? <Check size={12} className="text-purple-400" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Actions */}
          <div className="p-4 border-t border-neutral-900 bg-neutral-950/40 space-y-2">
            {twitchConn?.connected ? (
              <div className="flex gap-2">
                <button
                  onClick={handleTwitchSync}
                  className="flex-1 py-2 px-2 bg-purple-950/40 hover:bg-purple-900/50 border border-purple-700 text-purple-200 rounded-lg text-xs font-cairo transition-colors flex items-center justify-center gap-1"
                >
                  <Activity size={12} /> مزامنة التنبيهات
                </button>
                <button
                  onClick={handleTwitchDisconnect}
                  className="py-2 px-3 border border-red-900/60 hover:bg-red-950/30 text-red-400 rounded-lg text-xs font-cairo transition-colors flex items-center justify-center gap-1"
                >
                  <WifiOff size={12} /> قطع
                </button>
              </div>
            ) : (
              <a
                href={twitchOAuthUrl}
                className="w-full py-2 px-3 bg-[#9146FF]/20 hover:bg-[#9146FF]/30 border border-[#9146FF]/60 text-[#D8B4FE] rounded-lg text-xs font-black font-cairo transition-colors flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(145,70,255,0.15)]"
              >
                <ExternalLink size={13} /> تفعيل وربط حساب Twitch الآن
              </a>
            )}
          </div>
        </div>

        {/* ─── 3. TIKTOK PLATFORM CARD ─── */}
        <div className="border border-cyan-900/60 rounded-xl bg-neutral-950/90 overflow-hidden flex flex-col justify-between shadow-[0_0_25px_rgba(6,182,212,0.06)]">
          <div>
            {/* Platform Banner */}
            <div className="p-4 border-b border-neutral-800/80 bg-gradient-to-l from-cyan-950/40 to-transparent flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center font-black text-cyan-400 text-sm">
                  TT
                </div>
                <div>
                  <div className="font-bold text-sm text-white font-cairo flex items-center gap-1.5">
                    منصة TikTok LIVE
                    <span className="text-[10px] text-cyan-400 font-mono bg-cyan-950/60 border border-cyan-800 px-1.5 py-0.2 rounded">
                      OAuth 2.0
                    </span>
                  </div>
                  <div className="text-[10px] text-neutral-400 font-cairo">developers.tiktok.com</div>
                </div>
              </div>

              {/* Status Badge */}
              {tiktokConn?.connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-cyan-700 text-cyan-300 bg-cyan-950/50">
                  <CheckCircle2 size={11} /> متصل
                </span>
              ) : config.tiktok?.configured ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                  غير متصل
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-amber-800 text-amber-400 bg-amber-950/40">
                  <AlertCircle size={10} /> بانتظار المفاتيح
                </span>
              )}
            </div>

            {/* Platform Body */}
            <div className="p-4 space-y-3">
              {tiktokConn?.connected ? (
                <div className="p-3 border border-cyan-900/50 rounded-lg bg-cyan-950/20 space-y-1.5 text-xs font-cairo">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">اسم الحساب:</span>
                    <span className="text-cyan-300 font-bold">{tiktokConn.display_name || tiktokConn.username || tiktokConn.login}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">معرّف الحساب (Open ID):</span>
                    <span className="text-neutral-300 font-mono text-[10px] truncate max-w-[150px]">{tiktokConn.open_id || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">حالة البث:</span>
                    <span className={`font-bold ${tiktokConn.real_live ? "text-emerald-400" : "text-neutral-400"}`}>
                      {tiktokConn.real_live ? "🟢 مباشر" : "⚪ غير مباشر"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-neutral-400 font-cairo leading-relaxed">
                  ربط الحساب الرسمي عبر TikTok OAuth واستقبال تعليقات المشاهدين والتصويت المباشر تلقائياً.
                </p>
              )}

              {/* Live Webhook / Secret for Bridge */}
              {tiktokConn?.webhook_secret && (
                <div className="space-y-1 pt-2 border-t border-neutral-900">
                  <div className="text-[10px] text-neutral-500 font-cairo flex items-center justify-between">
                    <span>رابط الويب هوك الخاص بك (TikFinity / موصل خارجي):</span>
                  </div>
                  <div className="flex items-center gap-1 bg-black/50 border border-neutral-800 rounded px-2 py-1 text-[11px] font-mono text-neutral-300">
                    <span className="truncate flex-1" dir="ltr">{currentOrigin}/api/tiktok/webhook/{tiktokConn.webhook_secret}</span>
                    <button
                      onClick={() => copyToClipboard(`${currentOrigin}/api/tiktok/webhook/${tiktokConn.webhook_secret}`, "tt_wh")}
                      className="text-neutral-400 hover:text-white shrink-0 p-1"
                      title="نسخ الرابط"
                    >
                      {copiedKey === "tt_wh" ? <Check size={12} className="text-cyan-400" /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Stats */}
              {tiktokConn?.connected && (
                <div className="text-[10px] text-neutral-400 font-cairo flex items-center justify-between pt-1">
                  <span>التعليقات المستلمة: <strong className="text-cyan-300 font-mono">{tiktokConn.comment_count || 0}</strong></span>
                  <span>حالة الاتصال: <strong className="text-emerald-400">جاهز لاستقبال الأحداث</strong></span>
                </div>
              )}
            </div>
          </div>

          {/* Platform Actions */}
          <div className="p-4 border-t border-neutral-900 bg-neutral-950/40">
            {tiktokConn?.connected ? (
              <div className="flex gap-2">
                <button
                  onClick={handleTiktokReconnect}
                  className="flex-1 py-2 px-2 bg-neutral-900 hover:bg-neutral-800 border border-neutral-700 text-neutral-300 rounded-lg text-xs font-cairo transition-colors flex items-center justify-center gap-1"
                >
                  <RefreshCw size={12} /> إعادة تشغيل
                </button>
                <button
                  onClick={handleTiktokDisconnect}
                  className="py-2 px-3 border border-red-900/60 hover:bg-red-950/30 text-red-400 rounded-lg text-xs font-cairo transition-colors flex items-center justify-center gap-1"
                >
                  <WifiOff size={12} /> قطع
                </button>
              </div>
            ) : (
              <a
                href={tiktokOAuthUrl}
                className="w-full py-2 px-3 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/50 text-cyan-300 rounded-lg text-xs font-black font-cairo transition-colors flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.15)]"
              >
                <ExternalLink size={13} /> تفعيل وربط حساب TikTok الآن
              </a>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
